import {bitcore, bchaddrjs, bignumber, slpmdm as mdm} from "@ekliptor/payjs";
import {BchdRest, DUST_LIMIT, BchdRestOutput, BchdRestUtxo, MIN_FEE, BchUtxo} from "./BchdRest";
import * as utils from "./chain-utils";
import {logger} from "../utils/Log";
import {BigNumber} from "bignumber.js";
import {toSatoshis} from "./chain-utils";
import {PromptRest} from "./PromptRest"; // TODO use types from payjs

//export const MIN_FEE = 1000; // BCHD doesn't broadcast TX that are below this

export interface BitcoinCashAddress {
    address: string; // the address starting with "bitcoincash:"
    privateKey: string;
    wif: string;
}

export interface BchRecipient {
    bchAddress: string;
    amount: number; // in BCH (not satoshis). not BigNumber because bitcore.addOutput() doesn't accept it
}
export interface SlpTokenRecipient {
    slpAddress: string;
    tokenAmount: BigNumber;
}

export class BitcoinCash {
    protected privateKey: bitcore.PrivateKey = null;
    protected hdPrivateKey: bitcore.HDPrivateKey = null;
    protected bchd: PromptRest;

    constructor(myPrivateKey: string | bitcore.PrivateKey | bitcore.HDPrivateKey = "", bchd: PromptRest) {
        if (typeof myPrivateKey === "string")
            this.privateKey = new bitcore.PrivateKey(myPrivateKey ? myPrivateKey : undefined);
        else if (myPrivateKey instanceof bitcore.PrivateKey || myPrivateKey.deriveChild === undefined)
            this.privateKey = myPrivateKey as bitcore.PrivateKey;
        else {
            this.hdPrivateKey = myPrivateKey;
            //this.privateKey = myPrivateKey.deriveChild("m/0/0");
        }
        this.bchd = bchd;
    }

    public getPrivateKey() {
        return this.privateKey;
    }

    public getBchAddress(privateKey: bitcore.PrivateKey = null): string {
        if (privateKey === null)
            privateKey = this.getPrivateKey();
        const ownAddress = privateKey.toAddress().toString();
        return ownAddress;
    }

    /**
     * Set a another address than the default address of this Private Key.
     * Useful with BIP44 addresses.
     * @param address
     */
    /*
    public setBchAddress(address: string): void {
        this.bchdAddress = address;
    }*/

    public getSlpAddress(): string {
        return bchaddrjs.toSlpAddress(this.getBchAddress());
    }

    public getDerivedAddress(xpub: string, index: number): bitcore.Address {
        const pubKey = new bitcore.HDPublicKey(xpub);
        const hdPath = "m/0/" + index;
        let derivedAddress = new bitcore.Address(pubKey.deriveChild(hdPath, false).publicKey, bitcore.Networks.mainnet);
        return derivedAddress;
    }

    public static getDerivedKey(xpriv: string, index: number): bitcore.HDPrivateKey {
        const derivedHdKey = new bitcore.HDPrivateKey(xpriv);
        const hdPath = "m/0/" + index;
        return derivedHdKey.deriveChild(hdPath, false);
    }

    public async getBchBalance(): Promise<number> {
        return this.bchd.getAddressBalance(this.getBchAddress());
    }

    public async getSlpBalance(tokenID: string): Promise<number> {
        const slpAddress = this.getSlpAddress();
        return this.bchd.getAddressBalance(slpAddress, tokenID);
    }

    /**
     * Import an address from private key or WIF.
     * @param privateKeyHex
     * @return BitcoinCashAddress
     */
    public importAddress(privateKeyHex: string): BitcoinCashAddress {
        const key = this.extractPrivateKey(privateKeyHex);
        let privateKey = new bitcore.PrivateKey(key);
        return {
            address: privateKey.toAddress().toString(),
            privateKey: privateKey.toString(),
            wif: privateKey.toWIF()
        }
    }

    public async signTransaction(to: BchRecipient[], privateKey: bitcore.PrivateKey = null): Promise<bitcore.Transaction> {
        if (privateKey === null)
            privateKey = this.privateKey;
        const cashAddress = this.getBchAddress(privateKey);
        const utxo = await this.bchd.getBchAddressUnspentOutputs(cashAddress, true);
        return this.signTransactionWithUtxo(to, privateKey, cashAddress, utxo);
    }

    public async signTransactionWithUtxo(to: BchRecipient[], privateKey: bitcore.PrivateKey = null, cashAddress: string, utxo: BchUtxo): Promise<bitcore.Transaction> {
        if (privateKey === null)
            privateKey = this.privateKey;
        if (utxo.bchOutputs.length === 0)
            throw new Error(`Address ${cashAddress} has no BCH-only UTXO balance.`);

        // build the transaction
        let txn = new bitcore.Transaction();

        // add BCH to inputs
        const bchUtxo = this.bchdToBitcoreUtxo(utxo.bchOutputs, cashAddress);
        txn.from([...bchUtxo]);

        let script = bitcore.Script.empty();
        // @ts-ignore // Opcode is not part of typings
        //script.add(bitcore.Opcode.OP_RETURN);
        //txn.addOutput(new bitcore.Transaction.Output({script: script, satoshis: 0}));

        // add BCH recipients as outputs
        to.forEach((rec) => {
            //txn.to() // should work identically, see Transaction.prototype.to
            if (rec.bchAddress === cashAddress) {
                throw new Error("Receiver address can not be identical to change address: " + rec.bchAddress);
            }
            const sats = toSatoshis(rec.amount);
            //const script = bitcore.Script.fromAddress(rec.bchAddress);
            //txn.addOutput(new bitcore.Transaction.Output({satoshis: sats, script: script }));
            txn.to(rec.bchAddress, sats);
        });

        // change to sender
        txn.change(cashAddress)/*.feePerKb(MIN_FEE)*/; // bet bitcore handle fees automatically

        // sign the transaction
        txn = txn.sign(privateKey);
        logger.info("Signed TX with ID %s", txn.id);
        logger.info(txn);
        logger.info(txn.toString())
        //logger.verbose(txn.toString()); // full TX as hex

        return txn;
    }

    public async signSlpTransaction(tokenIdHex: string, to: SlpTokenRecipient[]): Promise<bitcore.Transaction> {
        // similar to https://github.com/simpleledgerinc/cpp_slp_graph_search/blob/master/regtest-bchn/test/helpers/rpcwallet.ts#L305
        // TODO is there a max number of recipients? max TX size... bitcore should already take care of that
        const slpAddress = this.getSlpAddress();
        const cashAddress = this.getBchAddress();
        // we have to include the mempool or else when signing many TX over a short time we get "output already spent" error
        const utxo = await this.bchd.getSlpAddressUnspentOutputs(slpAddress, tokenIdHex, true);
        if (utxo.bchOutputs.length === 0)
            throw new Error(`Address ${cashAddress} has no BCH-only UTXO balance to pay for SLP TX fees.`);
        else if (utxo.token_metadata === null)
            throw new Error(`No token meta data found for token ID ${tokenIdHex}`);

        // convert token amount to token "satoshis" (shift decimals)
        const decimals = utxo.token_metadata.type1.decimals;
        to.forEach((rec) => {
            rec.tokenAmount = rec.tokenAmount.multipliedBy(10**decimals, 10);
        });

        // build the transaction
        let txn = new bitcore.Transaction();

        // add BCH to inputs to pay TX fees
        const bchUtxo = this.bchdToBitcoreUtxo(utxo.bchOutputs, cashAddress);
        const slpUtxo = this.bchdToBitcoreUtxo(utxo.slpOutputs);
        txn.from([...slpUtxo, ...bchUtxo]);

        // add slp change back to our wallet
        const slpTotal = utxo.slpOutputs.reduce((p, c, _) => p.plus(c.slp_token!.amount), new BigNumber(0));
        //console.log("SLP TOTAL", slpTotal)
        const slpTo = to.reduce((p, c, _) => p.plus(c.tokenAmount), new BigNumber(0));
        to.unshift({ slpAddress: slpAddress, tokenAmount: slpTotal.minus(slpTo) }); // first address is our SLP change
        //console.log("SLP return", slpTotal.minus(slpTo))

        // add slp mint op_return
        // @ts-ignore
        const slpMsg = mdm.TokenType1.send(tokenIdHex, to.map((v, i, _) => v.tokenAmount));
        txn.addOutput(new bitcore.Transaction.Output({satoshis: 0, script: slpMsg }));

        // add dust outputs for token receiver and minting baton
        to.forEach((rec, i) => {
            const cashAddr = bchaddrjs.toCashAddress(rec.slpAddress);
            txn.to(cashAddr, DUST_LIMIT);
        });

        // change to sender
        txn.change(cashAddress);

        // sign the transaction
        txn = txn.sign(this.privateKey);
        logger.info("Signed TX with ID %s", txn.id);
        logger.info(txn);
        logger.info(txn.toString())
        //logger.verbose(txn.toString()); // full TX as hex

        return txn;
    }

    public async getAddressUtxos(addressStr: string, includeSlpMeta: boolean = false): Promise<bitcore.Transaction.UnspentOutput[]> {
        let utxos: bitcore.Transaction.UnspentOutput[] = [];
        let res = await this.bchd.getAddressUnspentOutputs(addressStr, includeSlpMeta);
        utxos = this.bchdToBitcoreUtxo(res.outputs, addressStr);
        return utxos;
    }

    public bchdToBitcoreUtxo(bchdOutputs: BchdRestOutput[], addressStr?: string): bitcore.Transaction.UnspentOutput[] {
        let outputs: bitcore.Transaction.UnspentOutput[] = [];
        for (let i = 0; i < bchdOutputs.length; i++)
        {
            let curOut = bchdOutputs[i];
            if (!curOut.outpoint)
                continue;
            const amount = typeof curOut.value === "number" ? curOut.value : parseFloat(curOut.value); // BCHD REST returns strings
            const script = Buffer.from(curOut.pubkey_script as string, "base64");
            const txid = Buffer.from(curOut.outpoint.hash as string, "base64");
            let bitcoreOut = bitcore.Transaction.UnspentOutput.fromObject({
                //txId: "115e8f72f39fad874cfab0deed11a80f24f967a84079fb56ddf53ea02e308986",
                txId: utils.reverseBytes(txid.toString("hex")),
                outputIndex: curOut.outpoint.index,
                address: addressStr ? new bitcore.Address(addressStr) : undefined,
                //script: new bitcore.Script("76a91447862fe165e6121af80d5dde1ecb478ed170565b88ac"),
                script: new bitcore.Script(script.toString("hex")),
                //script: this.getScriptPubKey(addressStr),
                satoshis: amount, // balance
            });
            outputs.push(bitcoreOut);
        }
        return outputs;
    }

    public findUtxoForTransaction(outputs: bitcore.Transaction.UnspentOutput[], minSats: number): bitcore.Transaction.UnspentOutput | null {
        for (let i = 0; i < outputs.length; i++)
        {
            if (outputs[i].satoshis >= minSats)
                return outputs[i];
        }
        return null;
    }

    // ################################################################
    // ###################### PRIVATE FUNCTIONS #######################

    protected extractPrivateKey(privateKeyHex: string): string {
        // ElectronCash shows keys as: address key - extract the 2nd part (key)
        // example simpleledger:qq... L1...
        const pos = privateKeyHex.indexOf(" ");
        if (pos === -1)
            return privateKeyHex;
        return privateKeyHex.substr(pos+1).trim();
    }
}
